import React, {useEffect, useState } from "react";
import "./index.css";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  addHazardAssessmentAnswer,
  savePreAssessmentReturnQuestion,
  updateHazardAssessmentAnswer,
} from "../../ducks/redux/actions/hazardAssessment";
import BackModal from "../Modal/BackModal";
import Button from "../../storybook/Button";
import HeaderJourney from "../../storybook/Header/HeaderJourney";
import Message from "../../storybook/Message";
import { showFooter, showMenuTabs } from "../../ducks/redux/actions/userChoices";
import ExitModalAndSave from "../Modal/ExitModalAndSave";

const EMPTY_ARRAY: any[] = [];

interface HazardAssessmentProps extends RouteComponentProps<any> {}

interface HazardResponse {
  code: string;
  text: string;
  score: number;
}

interface HazardQuestion {
  code: string;
  label: string;
  category: string;
  sequence: number;
  type: string;
  options: HazardResponse[];
  dependsOn: [];
}

export const getPreviousAnsweredSequence = (
  responses: any[],
  currentCode?: string,
  currentSequence?: number
) => {
  if (!responses?.length || !currentSequence) {
    return null;
  }

  const sortedResponses = [...responses].sort(
    (a: any, b: any) => Number(a.sequence) - Number(b.sequence)
  );

  if (currentCode) {
    const currentResponseIndex = sortedResponses.findIndex(
      (item: any) => item.code === currentCode
    );

    if (currentResponseIndex > 0) {
      return Number(sortedResponses[currentResponseIndex - 1].sequence);
    }
  }

  const previousSequence = sortedResponses
    .map((item: any) => Number(item.sequence))
    .filter((sequence: number) => Number.isFinite(sequence) && sequence < currentSequence)
    .sort((a: number, b: number) => b - a)[0];

  return previousSequence || null;
};

export const areAnswerCodesEqual = (left: any[] = [], right: any[] = []) => {
  if (left.length !== right.length) {
    return false;
  }

  const sortedLeft = [...left].map(String).sort((a, b) => a.localeCompare(b));
  const sortedRight = [...right].map(String).sort((a, b) => a.localeCompare(b));

  return sortedLeft.every((code, index) => code === sortedRight[index]);
};

export const isDependencyMatched = (question: any, responses: any[]): boolean => {
  if (!question.dependsOn?.length) {
    return false;
  }

  return question.dependsOn.some((dependency: any) => {
    const parentResponse = responses.find((resp: any) => resp.code === dependency.code);

    if (!parentResponse?.answerCodes?.length) {
      return false;
    }

    return dependency.responses.some((expectedCode: string) =>
      parentResponse.answerCodes.includes(expectedCode)
    );
  });
};

export const getNextQuestionIndex = (
  questions: any[],
  currentQuestion: any,
  updatedResponses: any[]
) => {
  const currentIndex = questions.findIndex(
    (q) => q.code === currentQuestion.code
  );

  const nextIndex = currentIndex + 1;
  const nextQuestion = questions[nextIndex];

  if (!nextQuestion) {
    return -1;
  }

  if (
    !nextQuestion.dependsOn?.length ||
    isDependencyMatched(nextQuestion, updatedResponses)
  ) {
    return nextIndex;
  }

  const matchingDependentIndex = questions.findIndex(
    (question, index) =>
      index > nextIndex &&
      question.dependsOn?.length &&
      isDependencyMatched(question, updatedResponses),
  );

  return matchingDependentIndex !== -1 ? matchingDependentIndex : nextIndex;
};

const HazardAssessment: React.FC<HazardAssessmentProps> = (props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const selectedRepairReasonObj = useSelector(
    (state: any) => state?.dashboardReducer?.formState?.selectedRepairReasonObject
  );
  
  const savedQuestions = useSelector((state: any) => state?.hazard?.hazardQuestions || EMPTY_ARRAY);
  const hazardResponses = useSelector((state: any) => state?.hazard?.responses || EMPTY_ARRAY);

  const [questions, setQuestions] = useState<HazardQuestion[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const { seq } = useParams<{ seq: string }>();
  const [error, setError] = useState<string>("");
  const [currentIndex, setCurrentIndex] = useState(Number(seq) - 1 || 0);
  const currentQuestion = questions?.[currentIndex];
  const preAssessmentResponses = hazardResponses.filter(
      (item: any) => item.isPreAssessment && item.answerCodes?.length
    );
    const selectedRepairReasonObject = useSelector((state: any) => state?.dashboardReducer?.formState?.selectedRepairReasonObject);
    const currentRepairReason = selectedRepairReasonObject?.[0];
  

  useEffect(() => {
    dispatch(showMenuTabs(false));
    dispatch(showFooter(false));
  }, [dispatch]);

  useEffect(() => {
    if (!currentQuestion) return;

    const existingAnswer = hazardResponses?.find(
      (item: any) => item.code === currentQuestion.code
    );

    if (existingAnswer) {
      setSelectedAnswers([...existingAnswer.answerCodes]); // spread to avoid mutating Redux state
    } else {
      setSelectedAnswers([]);
    }
  }, [currentQuestion, hazardResponses]);

  useEffect(() => {
    setQuestions(savedQuestions);
  }, [savedQuestions]);

  useEffect(() => {
    if (selectedRepairReasonObj?.[0]?.isHazard === false) {
      props.history.push("/RepairUploadmedia");
    }
  }, []);
  useEffect(() => {
    if (questions?.length && seq) {
      setCurrentIndex(Number(seq) - 1);
    }

    setShowAlert(
      selectedRepairReasonObj?.[0]?.isHazard === true && questions?.[currentIndex]?.sequence === 1
    );
  }, [seq, questions, currentIndex, selectedRepairReasonObj]);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, code: string) => {
    if (e.target.checked) {
      setSelectedAnswers((prev) => [...prev, code]);
    } else {
      setSelectedAnswers((prev) => prev.filter((c) => c !== code));
    }
    setError("");
  };

    const handleContinue = () => {
    if (selectedAnswers.length === 0) {
      setError(t("Hazard_Select_Answer_Error"));
      return;
    }

    setError("");

    const selectedResponseTexts = currentQuestion.options
      .filter((response: any) => selectedAnswers.includes(response.code))
      .map((response: any) => response.text);

    const payload = {
      sequence: currentQuestion.sequence,
      question: currentQuestion.label,
      answers: selectedResponseTexts,
      answerCodes: [...selectedAnswers], // spread to avoid mutation
      code: currentQuestion.code,
    };

    const existingQuestion = hazardResponses.some((item: any) => item.code === currentQuestion.code);

    const existingSavedAnswer = hazardResponses.find(
      (item: any) => item.code === currentQuestion.code
    );

    const hasAnswerChanged = !areAnswerCodesEqual(
      existingSavedAnswer?.answerCodes || [],
      selectedAnswers,
    );

    if (existingQuestion && hasAnswerChanged) {
      dispatch(updateHazardAssessmentAnswer(payload));
    } else if (!existingQuestion) {
      dispatch(addHazardAssessmentAnswer(payload));
    }

    const updatedResponses = existingQuestion
      ? hazardResponses.map((item: any) =>
          item.code === currentQuestion.code && hasAnswerChanged ? payload : item
        )
      : [...hazardResponses, payload];

    const nextIndex = getNextQuestionIndex(questions, currentQuestion, updatedResponses);

    if (nextIndex !== -1) {
      setCurrentIndex(nextIndex);
      setSelectedAnswers([]);
      setShowAlert(false);
      history.push(`/HazardAssessment/${nextIndex + 1}`);
    } else {
      history.push("/HazardReview");
    }
  };

  const backButtonHandler = () => {
    setError("");

    const previousSequence = getPreviousAnsweredSequence(
      hazardResponses,
      currentQuestion?.code,
      currentQuestion?.sequence
    );

    const currentSequence = Number(currentQuestion?.sequence);

    const gap =
      Number.isFinite(currentSequence) && Number.isFinite(Number(previousSequence))
        ? currentSequence - Number(previousSequence)
        : 0;

    if (gap > 0 && previousSequence !== null) {
      const previousIndexByGap = Math.max(0, currentIndex - gap);
      const prevPage = previousIndexByGap + 1;

      setCurrentIndex(previousIndexByGap);
      history.push(`/HazardAssessment/${prevPage}`);
      window.location.reload();
    } else if(currentRepairReason?.isPreAssessmentRequired === true && preAssessmentResponses.length){const lastPreAssessmentResponse = [...preAssessmentResponses]
            .sort((first: any, second: any) => second.sequence - first.sequence)?.[0];
          dispatch(savePreAssessmentReturnQuestion({ code: lastPreAssessmentResponse?.code || "", from: "UploadMedia" }));
            props.history.push("/HazardPreassessment");
    } else {
      props.history.push("/RepairAdditionalDetails");
    }
  };

  const exitButtonHandler = () => {
    history.push("/Customer360View");
  };

  const saveDraftAndExit = () => history.push("/customer360View");

  const renderCurrentQuestions = () => {
    if (!currentQuestion) return null;

    return (
      <div className="relative pb-2 padding-repair">
        <h3 className="text-xl sm:text-2xl font-semibold mb-2 sm:mb-6 pt-20 pb-12">
          {currentQuestion.label}
        </h3>

        {currentQuestion.type === "DROPDOWN" && (
          <div className="hazard-dropdown-wrap dropDownWidth">
            <select
              aria-label={currentQuestion.code}
              value={selectedAnswers[0] || ""}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedAnswers(val ? [val] : []);
                if (val) setError("");
              }}
              className={`hazard-dropdown ${error ? "has-error" : ""}`}
            >
              <option value="">Select an option</option>
              {currentQuestion.options?.map((option: any) => (
                <option key={option.code} value={option.code}>
                  {option.text}
                </option>
              ))}
            </select>
            <span className="hazard-dropdown-arrow" aria-hidden="true">
              ▾
            </span>
          </div>
        )}

        {currentQuestion.type === "RADIO" && (
          <div className="grid grid-cols-1 gap-2 dropDownWidth">
            {currentQuestion.options?.map((option: any) => {
              const isChecked = selectedAnswers.includes(option.code);

              return (
                <label
                  key={option.code}
                  htmlFor={`${currentQuestion.code}-${option.code}`}
                  className={`hazard-option ${isChecked ? "hazard-option--selected" : ""}`}
                >
                  <input
                    type="radio"
                    id={`${currentQuestion.code}-${option.code}`}
                    name={currentQuestion.code}
                    value={option.code}
                    className="hazard-option__input"
                    checked={isChecked}
                    onChange={() => {
                      setSelectedAnswers([option.code]);
                      setError("");
                    }}
                    aria-label={option.text}
                  />
                  <span className="hazard-option__text">{option.text}</span>
                </label>
              );
            })}
          </div>
        )}

        {currentQuestion.type === "CHECKBOX" && (
          <div className="grid grid-cols-1 gap-2 dropDownWidth">
            {currentQuestion.options?.map((option: any) => {
              const isChecked = selectedAnswers.includes(option.code);

              return (
                <label
                  key={option.code}
                  htmlFor={`${currentQuestion.code}-${option.code}`}
                  className={`hazard-option ${isChecked ? "hazard-option--selected" : ""}`}
                >
                  <input
                    type="checkbox"
                    id={`${currentQuestion.code}-${option.code}`}
                    name={currentQuestion.code}
                    value={option.code}
                    className="hazard-option__input hazard-option__input--checkbox"
                    checked={isChecked}
                    onChange={(e) => handleCheckboxChange(e, option.code)}
                    aria-label={option.text}
                  />
                  <span className="hazard-option__text">{option.text}</span>
                </label>
              );
            })}
          </div>
        )}

        {error && (
          <div data-testid="alert-warning">
            <Message className="warning-msg">{t(error)}</Message>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <HeaderJourney />
      <div className="container-fluid parent-footer repair-request p-0" id="repAddDet-container">
        {showAlert && (
          <div role="alert" data-testid="alert-warning" className="additional-infomsg pt-16 padding-repair">
            <Message className="info-msg">{t("Hazard_Assessment_Alert")}</Message>
          </div>
        )}

        <div className="px-4 sm:px-0 mb-4">{questions && renderCurrentQuestions()}</div>

        <div className="col-md-12 marginTop-auto" id="repAddDet-div5">
          <div className="row m-0 footer-actionbtn">
            <div className="col btn-top padding-repair mb-0">
              <BackModal
                id="repAddDet-backmodal"
                back={backButtonHandler}
              />
              <ExitModalAndSave
                id="repair-asset-exitmodal"
                exit={exitButtonHandler}
                saveDraftAndExit={saveDraftAndExit}
              />
            </div>

            <div className="col text-end pt-24 padding-repair">
              <Button
                id="btn_hhrs_continue"
                onClick={handleContinue}
                disabled={error!==""}
                className="primary-btn"
              >
                {t("continuen_button")}{" "}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HazardAssessment;